import { zodResolver } from "@hookform/resolvers/zod";
import { Box, CircularProgress } from "@mui/material";
import { extractErrorMessage, useInvalidateQuery } from "@platform/api";
import {
  createReleaseMutation,
  createTestGroupMutation,
  listTestGroupsOptions,
  type ReleaseOut,
} from "@platform/api/golden";
import {
  Alert,
  Button,
  Card,
  ErrorBoundary,
  TextField,
  Typography,
} from "@platform/design-system";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";

import { useGoldenClient } from "../api/GoldenClientProvider";
import { GOLDEN_TAGS } from "../api/goldenTags";
import { usePageTitle } from "../hooks/usePageTitle";
import { TIER_IN_TEXT } from "../tier";

import { TestGroupCard } from "./TestGroupCard";

import type { ReactNode } from "react";

export const TEST_GROUPS_TEST_ID = "golden-test-groups";

// The names the API accepts (TestGroupCreate.name in the golden spec).
const GROUP_NAME = /^[a-z0-9][a-z0-9._-]*$/;
const GROUP_NAME_MAX_LENGTH = 255;

const groupSchema = z.object({
  name: z
    .string()
    .trim()
    .max(GROUP_NAME_MAX_LENGTH)
    .regex(
      GROUP_NAME,
      "Lowercase letters, digits, dots, dashes, and underscores, starting with a letter or digit",
    ),
  description: z.string().trim(),
});

type GroupValues = z.infer<typeof groupSchema>;

const suiteSchema = z.object({
  suiteId: z
    .string()
    .trim()
    .regex(/^#?[1-9][0-9]*$/, "Enter a suite id, for example 8")
    .transform((value) => value.replace(/^#/, "")),
});

type SuiteInput = z.input<typeof suiteSchema>;
type SuiteValues = z.output<typeof suiteSchema>;

const releaseNotice = (release: ReleaseOut): string => {
  const skipped = release.skipped_case_ids ?? [];
  const skippedText =
    skipped.length > 0
      ? `, ${skipped.length} skipped because they are not ${TIER_IN_TEXT}`
      : "";
  return `Release ${release.name}: ${release.cases.length} case(s) frozen${skippedText}.`;
};

const CreateGroupForm = (): ReactNode => {
  const client = useGoldenClient();
  const invalidateQuery = useInvalidateQuery();
  const { control, handleSubmit, reset } = useForm<GroupValues>({
    resolver: zodResolver(groupSchema),
    defaultValues: { name: "", description: "" },
  });
  const createGroup = useMutation({
    ...createTestGroupMutation({ client }),
    onSuccess: async () => {
      reset();
      await invalidateQuery({ tags: [...GOLDEN_TAGS.testGroups] });
    },
  });

  const submit = handleSubmit(({ name, description }) =>
    createGroup.mutate({ body: { name, description: description || null } }),
  );

  return (
    <Card
      data-testid={`${TEST_GROUPS_TEST_ID}-create`}
      title="Create a test group"
    >
      <Box
        component="form"
        noValidate
        onSubmit={(event) => void submit(event)}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        {createGroup.isError && (
          <Alert
            data-testid={`${TEST_GROUPS_TEST_ID}-create-error`}
            severity="error"
          >
            {extractErrorMessage(
              createGroup.error,
              "Failed to create the group",
            )}
          </Alert>
        )}
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          {/* The design-system fields forward their ref to the wrapper, not the input,
              so they are controlled rather than registered. */}
          <Controller
            control={control}
            name="name"
            render={({ field: { ref: _ref, ...field }, fieldState }) => (
              <TextField
                data-testid={`${TEST_GROUPS_TEST_ID}-name`}
                label="Name"
                placeholder="dental-networks"
                required
                error={fieldState.error !== undefined}
                helperText={fieldState.error?.message}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="description"
            render={({ field: { ref: _ref, ...field } }) => (
              <TextField
                data-testid={`${TEST_GROUPS_TEST_ID}-description`}
                label="What the group covers"
                sx={{ flex: 1, minWidth: 280 }}
                {...field}
              />
            )}
          />
          <Button
            type="submit"
            data-testid={`${TEST_GROUPS_TEST_ID}-create-submit`}
            loading={createGroup.isPending}
          >
            Create group
          </Button>
        </Box>
      </Box>
    </Card>
  );
};

const OpenSuiteForm = (): ReactNode => {
  const navigate = useNavigate();
  const { control, handleSubmit } = useForm<SuiteInput, unknown, SuiteValues>({
    resolver: zodResolver(suiteSchema),
    defaultValues: { suiteId: "" },
  });

  const submit = handleSubmit(({ suiteId }) => navigate(`/suites/${suiteId}`));

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(event) => void submit(event)}
      sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
    >
      <Controller
        control={control}
        name="suiteId"
        render={({ field: { ref: _ref, ...field }, fieldState }) => (
          <TextField
            data-testid={`${TEST_GROUPS_TEST_ID}-suite-id`}
            label="Suite id"
            slotProps={{ htmlInput: { inputMode: "numeric" } }}
            error={fieldState.error !== undefined}
            helperText={fieldState.error?.message}
            {...field}
          />
        )}
      />
      <Button
        type="submit"
        variant="secondary"
        data-testid={`${TEST_GROUPS_TEST_ID}-open-suite`}
      >
        Open suite report
      </Button>
    </Box>
  );
};

/** The test groups suites run on, with the forms to create one, fill it, and freeze it. */
export const TestGroupsPage = (): ReactNode => {
  usePageTitle("Test groups");
  const client = useGoldenClient();
  const groups = useQuery(listTestGroupsOptions({ client }));
  const createRelease = useMutation(createReleaseMutation({ client }));
  const freezingGroup = createRelease.isPending
    ? createRelease.variables.path.name
    : undefined;

  return (
    <Box
      data-testid={TEST_GROUPS_TEST_ID}
      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
    >
      <Typography variant="header-large" component="h1">
        Test groups
      </Typography>
      <Typography variant="body-medium" color="textSecondary">
        A suite runs on one test group. When it starts, the group is frozen as a
        release: its {TIER_IN_TEXT} cases at their current correction revisions.
      </Typography>
      <ErrorBoundary>
        <CreateGroupForm />
      </ErrorBoundary>
      <OpenSuiteForm />
      {createRelease.isSuccess && (
        <Alert
          data-testid={`${TEST_GROUPS_TEST_ID}-release`}
          severity="success"
        >
          {releaseNotice(createRelease.data)}
        </Alert>
      )}
      {createRelease.isError && (
        <Alert
          data-testid={`${TEST_GROUPS_TEST_ID}-release-error`}
          severity="error"
        >
          {extractErrorMessage(
            createRelease.error,
            "Failed to freeze the group",
          )}
        </Alert>
      )}
      {groups.isSuccess ? (
        groups.data.map((group) => (
          <ErrorBoundary key={group.id}>
            <TestGroupCard
              group={group}
              freezing={freezingGroup === group.name}
              onFreeze={(name) =>
                createRelease.mutate({ path: { name }, body: {} })
              }
            />
          </ErrorBoundary>
        ))
      ) : groups.isError ? (
        <Alert data-testid={`${TEST_GROUPS_TEST_ID}-error`} severity="error">
          {extractErrorMessage(groups.error, "Failed to load the test groups")}
        </Alert>
      ) : (
        <CircularProgress aria-label="Loading the test groups" />
      )}
    </Box>
  );
};
